"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import styles from "../css/vitals-observations.module.css";
import BmiBenefitCheckButton from "../../../../components/BmiBenefitCheckButton";
import {
  getConsultationFull,
} from "../../../../api/clinic/consultationApi.js";

export default function VitalsObservations() {
  const navigate = useNavigate();
  const { consultationData, previousStep, nextStep, goToStep, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const blockNegativeNumberKeys = (e) => {
    if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
      e.preventDefault();
    }
  };

  // Set current step to 3 when component mounts
  useEffect(() => {
    goToStep(3);
  }, [goToStep]);

  const draftKey = "vitalsObservations";

  const defaultFormData = {
    // Core Vitals
    temperature: "",
    pulse: "",
    respiration: "",
    glucose: "",
    bpSystolic: "",
    bpDiastolic: "",
    
    // Body Measurements
    weight: "",
    height: "",
    bmi: "",
    cholesterol: "",
    hemoglobin: "",
    
    // Physical Observation (Toggles)
    observations: {
      hydration: false,
      jaundice: false,
      anaemia: false,
      cyanosis: false,
      clubbing: false,
      oedema: false,
      lymphNodes: false
    }
  };

  const [formData, setFormData] = useState(() => getStepDraft(draftKey, defaultFormData));

  // Auto-calculate BMI
  useEffect(() => {
    if (formData.height && formData.weight) {
      const heightInMeters = formData.height / 100;
      const calculatedBmi = (formData.weight / (heightInMeters * heightInMeters)).toFixed(1);
      setFormData((prev) => {
        const next = { ...prev, bmi: calculatedBmi };
        setStepDraft(draftKey, next);
        return next;
      });
    } else if (formData.bmi) {
      // Height or weight was cleared — the last computed BMI is now stale
      // (and possibly wildly wrong, e.g. from a momentary 1cm height while
      // typing "180"), so clear it rather than leaving it displayed.
      setFormData((prev) => {
        const next = { ...prev, bmi: "" };
        setStepDraft(draftKey, next);
        return next;
      });
    }
  }, [formData.height, formData.weight]);

  useEffect(() => {
    let cancelled = false;
    (async () => {

      try {
        const id = consultationData?.consultationId;
        if (!id) return;
        const full = await getConsultationFull(id);
        const v = full?.consultation_vitals;
        if (!v || cancelled) return;

        const existingDraft = getStepDraft(draftKey, null);
        if (existingDraft) return;

        const next = {
          ...defaultFormData,
          temperature: v.temperature ?? "",
          pulse: v.pulse ?? "",
          respiration: v.respiration ?? "",
          glucose: v.glucose ?? "",
          bpSystolic: v.bpsystolic ?? "",
          bpDiastolic: v.bpdiastolic ?? "",
          weight: v.weight ?? "",
          height: v.height ?? "",
          bmi: v.bmi ?? "",
          cholesterol: v.cholesterol ?? "",
          hemoglobin: v.hemoglobin ?? "",
          observations: {
            hydration: !!v.hydration,
            jaundice: !!v.jaundice,
            anaemia: !!v.anaemia,
            cyanosis: !!v.cyanosis,
            clubbing: !!v.clubbing,
            oedema: !!v.oedema,
            lymphNodes: !!v.lymphnodes,
          },
        };

        setFormData(next);
        setStepDraft(draftKey, next);
      } catch (e) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.consultationId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue = typeof value === "string" && value.trim().startsWith("-") ? "" : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handleToggleChange = (name) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        observations: {
          ...prev.observations,
          [name]: !prev.observations[name],
        },
      };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    const parseOptionalNumber = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = Number(val);
      if (!Number.isFinite(num)) return NaN;
      return num;
    };

    const validateNonNegative = (field, label) => {
      const num = parseOptionalNumber(formData[field]);
      if (num === null) return;
      if (!Number.isFinite(num)) {
        nextErrors[field] = `${label} must be a number.`;
        return;
      }
      if (num < 0) nextErrors[field] = `${label} cannot be negative.`;
    };

    validateNonNegative("temperature", "Temperature");
    validateNonNegative("pulse", "Pulse");
    validateNonNegative("respiration", "Respiration");
    validateNonNegative("glucose", "Glucose");
    validateNonNegative("bpSystolic", "BP (Systolic)");
    validateNonNegative("bpDiastolic", "BP (Diastolic)");
    validateNonNegative("weight", "Weight");
    validateNonNegative("height", "Height");
    validateNonNegative("cholesterol", "Cholesterol");
    validateNonNegative("hemoglobin", "Hemoglobin (HB)");

    const height = parseOptionalNumber(formData.height);
    if (height !== null && Number.isFinite(height) && height === 0) {
      nextErrors.height = "Height must be greater than 0.";
    }

    const sys = parseOptionalNumber(formData.bpSystolic);
    const dia = parseOptionalNumber(formData.bpDiastolic);
    if (
      sys !== null &&
      dia !== null &&
      Number.isFinite(sys) &&
      Number.isFinite(dia) &&
      dia > sys
    ) {
      nextErrors.bpDiastolic = "BP (Diastolic) cannot be greater than BP (Systolic).";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      window.alert(Object.values(nextErrors).join("\n"));
      return false;
    }
    return true;
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/medical-social-history");
  };

  const handleNext = async () => {
    try {
      if (!validate()) return;
      setSaving(true);
      nextStep();
      navigate("/customer/clinic/symptoms-exam");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={3}
            totalSteps={10}
            stepTitle="Vitals & Observations"
            stepDetails="Step 3 of 10 · Maria van der Merwe · General Consultation"
            onPrevious={handlePrevious}
            onNext={handleNext}
            nextDisabled={saving}
          />

          <div className={styles.formContainer}>
            {/* SECTION 1: CORE VITALS */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>CORE VITALS</h3>
              <div className={styles.vitalsGrid}>
                <div className={styles.inputGroup}>
                  <label>Temperature</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="temperature" value={formData.temperature} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>°C</span>
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>Pulse</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="pulse" value={formData.pulse} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>/min</span>
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>Respiration</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="respiration" value={formData.respiration} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>/min</span>
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>Glucose</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="glucose" value={formData.glucose} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>mmol/L</span>
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>BP (Systolic)</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="bpSystolic" value={formData.bpSystolic} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>mmHG</span>
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>BP (Diastolic)</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="bpDiastolic" value={formData.bpDiastolic} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>mmHG</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2: BODY MEASUREMENTS */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>BODY MEASUREMENTS</h3>
              <div className={styles.measurementsGrid}>
                <div className={styles.inputGroup}>
                  <label>Weight</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="weight" value={formData.weight} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>kg</span>
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>Height</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="height" value={formData.height} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>cm</span>
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>BMI (auto-calculated)</label>
                  <div className={styles.inputWrapper}>
                    <input type="text" value={formData.bmi} readOnly placeholder="—" className={styles.readOnlyInput} />
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>Cholesterol</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="cholesterol" value={formData.cholesterol} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>mmol/L</span>
                  </div>
                </div>
                {formData.bmi && (
                  <div className={styles.medicalAidCheck}>
                    <BmiBenefitCheckButton
                      patientId={consultationData?.patientId}
                      bmi={formData.bmi}
                    />
                  </div>
                )}
                <div className={styles.inputGroup}>
                  <label>Hemoglobin (HB)</label>
                  <div className={styles.inputWrapper}>
                    <input type="number" name="hemoglobin" value={formData.hemoglobin} onChange={handleInputChange} onKeyDown={blockNegativeNumberKeys} placeholder="—" min={0} />
                    <span className={styles.unit}>g/dL</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 3: PHYSICAL OBSERVATION / EXAMINATION */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>PHYSICAL OBSERVATION / EXAMINATION</h3>
              <div className={styles.observationsGrid}>
                {Object.keys(formData.observations).map((key) => (
                  <div key={key} className={styles.observationItem}>
                    <label className={styles.switch}>
                      <input 
                        type="checkbox" 
                        checked={formData.observations[key]} 
                        onChange={() => handleToggleChange(key)} 
                      />
                      <span className={styles.slider}></span>
                    </label>
                    <span className={styles.obsLabel}>{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}